import React, { useEffect, useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import httpService from '../../services/httpService';
import {
  ApiEndpoints,
  ImportTemplates,
  Constants
} from '../../constants/appConstants';
import { useExcelImportPopup } from '../Common/useExcelImportPopup';
import { useDataManagementFileUpload } from '../Common/useDataManagementFileUpload';
import { createDialogOverlayStyles } from '../../utils/safeCssInjection';
import DataManagementForm from '../DataManagement/DataManagementForm';
import {
  dataManagementFormConfig,
  getFormValidationErrors,
  normalizeFieldValues
} from '../DataManagement/dataManagementFormConfig';
import DataManagementLoader from '../DataManagement/DataManagementLoader';
import SafeHtml from '../Common/SafeHtml';
import logger from '../../utils/logger';
import './SmaComponent.scss';

const fetchPerformanceStandards = (providerType?: string) => {
  let url = ApiEndpoints.GetAllPerformanceStandardsWithInactive;

  if (providerType) {
    url += `?providerType=${encodeURIComponent(providerType)}`;
  }

  return httpService.get(url);
};

interface SmaComponentProps {
  providerTypes: { label: string; value: string }[];
}

const SmaComponent: React.FC<SmaComponentProps> = ({
  providerTypes
}) => {
  React.useEffect(() => {
    const cleanup = createDialogOverlayStyles(
      'sma-component-styles'
    );

    return cleanup;
  }, []);

  const [tiers, setTiers] = useState<any[]>([]);
  const [measures, setMeasures] = useState<any[]>([]);
  const [allStandards, setAllStandards] = useState<any[]>([]);
  const [performanceAreas, setPerformanceAreas] = useState<any[]>([]);
  const [measureStatuses, setMeasureStatuses] = useState<any[]>([]);
  const [compositeGroups, setCompositeGroups] = useState<any[]>([]);
  const [lookupsLoading, setLookupsLoading] = useState(true);

  useEffect(() => {
    const lookupRequests = [
      httpService
        .get(ApiEndpoints.GetTiersWithInactive)
        .then((r: any) =>
          setTiers(Array.isArray(r.data) ? r.data : [])
        )
        .catch((error: any) => {
          logger.error(
            'SmaComponent.GetTiersWithInactive failed',
            error
          );
          setTiers([]);
        }),

      httpService
        .get(ApiEndpoints.GetAllMeasuresWithInactive)
        .then((r: any) =>
          setMeasures(Array.isArray(r.data) ? r.data : [])
        )
        .catch((error: any) => {
          logger.error(
            'SmaComponent.GetAllMeasuresWithInactive failed',
            error
          );
          setMeasures([]);
        }),

      httpService
        .get(ApiEndpoints.GetAllStandards)
        .then((r: any) =>
          setAllStandards(Array.isArray(r.data) ? r.data : [])
        )
        .catch((error: any) => {
          logger.error(
            'SmaComponent.GetAllStandards failed',
            error
          );
          setAllStandards([]);
        }),

      httpService
        .get(ApiEndpoints.GetAllPerformanceAreasWithInactive)
        .then((r: any) =>
          setPerformanceAreas(Array.isArray(r.data) ? r.data : [])
        )
        .catch((error: any) => {
          logger.error(
            'SmaComponent.GetAllPerformanceAreasWithInactive failed',
            error
          );
          setPerformanceAreas([]);
        }),

      httpService
        .get(ApiEndpoints.GetMeasureStatuses)
        .then((r: any) =>
          setMeasureStatuses(Array.isArray(r.data) ? r.data : [])
        )
        .catch((error: any) => {
          logger.error(
            'SmaComponent.GetMeasureStatuses failed',
            error
          );
          setMeasureStatuses([]);
        }),

      httpService
        .get(ApiEndpoints.GetCompositeMeasureGroups)
        .then((r: any) =>
          setCompositeGroups(Array.isArray(r.data) ? r.data : [])
        )
        .catch((error: any) => {
          logger.error(
            'SmaComponent.GetCompositeMeasureGroups failed',
            error
          );
          setCompositeGroups([]);
        })
    ];

    Promise.all(lookupRequests).finally(() =>
      setLookupsLoading(false)
    );
  }, []);

  const [standards, setStandards] = useState<any[]>([]);
  const toast = useRef<Toast>(null);
  const { uploadFile } = useDataManagementFileUpload();

  const handleExcelUpload = async (file: File) => {
    setLoading(true);

    try {
      const fileKey = await uploadFile(
        file,
        Constants.DATA_MANAGEMENT_MODULES.PERFORMANCE_STANDARDS
      );

      await httpService.post(
        ApiEndpoints.BulkUploadDataManagement,
        {
          fileKey,
          fileName: file.name,
          module:
            Constants.DATA_MANAGEMENT_MODULES.PERFORMANCE_STANDARDS
        }
      );

      const response: any =
        await fetchPerformanceStandards();

      let data = response.data;

      if (!Array.isArray(data)) {
        data = [];
      }

      setStandards(data);

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Performance standards uploaded successfully',
        life: 3000
      });
    } catch (error: any) {
      logger.error(
        'SmaComponent.handleExcelUpload failed',
        error
      );

      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail:
          error?.response?.data?.message ||
          'Bulk upload failed',
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const { showImport, ImportPopup } =
    useExcelImportPopup({
      templateUrl:
        ImportTemplates.standardMeasureAssociation,
      onUpload: handleExcelUpload,
      title: 'Import Performance Standards from Excel'
    });

  const [providerTypeFilter, setProviderTypeFilter] =
    useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editDialogVisible, setEditDialogVisible] =
    useState(false);
  const [selectedStandard, setSelectedStandard] =
    useState<any>(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState(false);
  const [standardToDelete, setStandardToDelete] =
    useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const rowsPerPage = 7;

  const loadData = () => {
    setLoading(true);
    setError('');

    fetchPerformanceStandards()
      .then((response: any) => {
        let data = response.data;

        if (!Array.isArray(data)) {
          data = [];
        }

        setStandards(data);
      })
      .catch((error: any) => {
        logger.error(
          'SmaComponent.loadData failed',
          error
        );

        setError('Failed to fetch performance standards.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  const providerTypeOptions =
    providerTypes && providerTypes.length > 0
      ? providerTypes
      : Array.from(
          new Set(
            standards
              .map((s) => s.providerType)
              .filter(Boolean)
          )
        ).map((pt) => ({
          label: pt,
          value: pt
        }));

  let filteredStandards = standards;

  if (providerTypeFilter) {
    filteredStandards = filteredStandards.filter(
      (s) => s.providerType === providerTypeFilter
    );
  }

  if (search.trim()) {
    const q = search.trim().toLowerCase();

    filteredStandards = filteredStandards.filter(
      (s) =>
        (s.measureNumber &&
          s.measureNumber.toLowerCase().includes(q)) ||
        (s.standardTitle &&
          s.standardTitle.toLowerCase().includes(q)) ||
        (s.performanceAreaTitle &&
          s.performanceAreaTitle.toLowerCase().includes(q))
    );
  }

  const totalPages = Math.ceil(
    filteredStandards.length / rowsPerPage
  );

  const paginatedStandards = filteredStandards.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const handleSave = async () => {
    if (!selectedStandard) {
      return;
    }

    const validationErrors = getFormValidationErrors(
      dataManagementFormConfig.measureMapping,
      selectedStandard
    );

    if (validationErrors.length > 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Fix the following',
        detail: validationErrors.join(', '),
        life: 4000
      });

      return;
    }

    const payload = normalizeFieldValues(
      dataManagementFormConfig.measureMapping,
      selectedStandard
    );

    try {
      setLoading(true);

      if (isAddMode) {
        await httpService.post(
          ApiEndpoints.AddPerformanceStandard,
          payload
        );

        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Performance standard added successfully',
          life: 3000
        });
      } else {
        await httpService.put(
          `${ApiEndpoints.UpdatePerformanceStandard}/${selectedStandard.standardMeasureId}`,
          payload
        );

        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Performance standard updated successfully',
          life: 3000
        });
      }

      setEditDialogVisible(false);
      setIsAddMode(false);
      loadData();
    } catch (error: any) {
      logger.error(
        'SmaComponent.handleSave failed',
        error
      );

      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail:
          error?.response?.data?.message ||
          'Save failed',
        life: 3000
      });

      setTimeout(() => {
        setEditDialogVisible(false);
        setIsAddMode(false);
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!standardToDelete) {
      return;
    }

    setStandards(
      standards.filter(
        (s) =>
          s.standardMeasureId !==
          standardToDelete.standardMeasureId
      )
    );

    setDeleteDialogVisible(false);
    setStandardToDelete(null);
  };

  return (
    <div
      style={{
        marginTop: 24,
        position: 'relative',
        minHeight: 220
      }}
      aria-busy={loading || lookupsLoading}
    >
      <DataManagementLoader
        visible={loading || lookupsLoading}
        label="Loading performance standards"
      />

      <Toast
        ref={toast}
        position="top-right"
        style={{ zIndex: 9999 }}
      />

      {error && (
        <div style={{ color: 'red' }}>
          {error}
        </div>
      )}

      {ImportPopup}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 16,
          gap: 16
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 12
          }}
        >
          <Button
            label="Add New"
            icon="pi pi-plus"
            onClick={() => {
              setSelectedStandard({
                standardMeasureId: 0,
                performanceAreaTitle: '',
                tierName: '',
                standardTitle: '',
                measureNumber: '',
                measureTitle: '',
                measureDescription: '',
                isActive: true,
                scoringCriteria: '',
                measureStatusName: '',
                compositeGroupName: '',
                providerType: '',
                reviewYear: '',
                reviewConfigId: null
              });

              setIsAddMode(true);
              setEditDialogVisible(true);
            }}
          />

          <Button
            label="Import"
            icon="pi pi-upload"
            className="p-button-secondary"
            onClick={showImport}
            style={{ marginLeft: 8 }}
          />
        </div>

        <div className="sma-search-anim">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />

          <span className="pi pi-search" />
        </div>

        <div className="provider-type-dropdown">
          <select
            value={providerTypeFilter}
            onChange={(e) => {
              setProviderTypeFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">
              All Provider Types
            </option>

            {providerTypeOptions.map((opt) => (
              <option
                key={opt.value}
                value={opt.value}
              >
                {opt.label}
              </option>
            ))}
          </select>

          <span className="pi pi-angle-down" />
        </div>
      </div>

      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          background: '#fff'
        }}
      >
        <thead>
          <tr style={{ background: '#f0f4f8' }}>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 50 }}>#</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be' }}>Performance Area Title</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be' }}>Tier Name</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be' }}>Standard Title</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be' }}>Measure Number</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be' }}>Measure Title</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be' }}>Measure Description</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be' }}>Is Active</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be' }}>Scoring Criteria</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be' }}>Measure Status</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be' }}>Composite Group</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be' }}>Provider Type</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be' }}>Review Year</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 140 }}>Actions</th>
          </tr>
        </thead>

        <tbody>
          {paginatedStandards.length === 0 ? (
            <tr>
              <td
                colSpan={14}
                style={{
                  textAlign: 'center',
                  padding: 16
                }}
              >
                No performance standards found.
              </td>
            </tr>
          ) : (
            paginatedStandards.map(
              (s: any, idx: number) => (
                <tr
                  key={s.standardMeasureId || idx}
                  style={{
                    background:
                      idx % 2 === 0
                        ? '#fff'
                        : '#f6f8fa'
                  }}
                >
                  <td style={{ padding: 8, border: '1px solid #eee' }}>
                    {(currentPage - 1) * rowsPerPage + idx + 1}
                  </td>

                  <td style={{ padding: 8, border: '1px solid #eee' }}>
                    {s.performanceAreaTitle || '-'}
                  </td>

                  <td style={{ padding: 8, border: '1px solid #eee' }}>
                    {s.tierName || '-'}
                  </td>

                  <td style={{ padding: 8, border: '1px solid #eee' }}>
                    {s.standardTitle || '-'}
                  </td>

                  <td style={{ padding: 8, border: '1px solid #eee' }}>
                    {s.measureNumber || '-'}
                  </td>

                  <td style={{ padding: 8, border: '1px solid #eee' }}>
                    {s.measureTitle || '-'}
                  </td>

                  <td
                    style={{
                      padding: 8,
                      border: '1px solid #eee',
                      maxWidth: 300,
                      whiteSpace: 'pre-line',
                      wordBreak: 'break-word'
                    }}
                  >
                    <SafeHtml
                      html={
                        s.measureDescriptionHtml ??
                        '-'
                      }
                    />
                  </td>

                  <td style={{ padding: 8, border: '1px solid #eee' }}>
                    {s.isActive ? 'Yes' : 'No'}
                  </td>

                  <td
                    style={{
                      padding: 8,
                      border: '1px solid #eee',
                      maxWidth: 250
                    }}
                  >
                    <SafeHtml
                      html={s.scoringCriteria ?? '-'}
                    />
                  </td>

                  <td style={{ padding: 8, border: '1px solid #eee' }}>
                    {s.measureStatusName || '-'}
                  </td>

                  <td style={{ padding: 8, border: '1px solid #eee' }}>
                    {s.compositeGroupName || '-'}
                  </td>

                  <td style={{ padding: 8, border: '1px solid #eee' }}>
                    {s.providerType || '-'}
                  </td>

                  <td style={{ padding: 8, border: '1px solid #eee' }}>
                    {s.reviewYear || '-'}
                  </td>

                  <td style={{ padding: 8, border: '1px solid #eee' }}>
                    <Button
                      icon="pi pi-pencil"
                      className="p-button-text"
                      style={{ marginRight: 8 }}
                      onClick={() => {
                        setSelectedStandard({ ...s });
                        setIsAddMode(false);
                        setEditDialogVisible(true);
                      }}
                    />

                    <Button
                      icon="pi pi-trash"
                      className="p-button-text p-button-danger"
                      onClick={() => {
                        setStandardToDelete(s);
                        setDeleteDialogVisible(true);
                      }}
                    />
                  </td>
                </tr>
              )
            )
          )}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div
          style={{
            marginTop: 16,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 8
          }}
        >
          <Button
            icon="pi pi-angle-left"
            className="p-button-text"
            disabled={currentPage <= 1}
            onClick={() =>
              setCurrentPage((p) =>
                Math.max(1, p - 1)
              )
            }
          />

          <span>
            Page {currentPage} of {totalPages}
          </span>

          <Button
            icon="pi pi-angle-right"
            className="p-button-text"
            disabled={currentPage >= totalPages}
            onClick={() =>
              setCurrentPage((p) =>
                Math.min(totalPages, p + 1)
              )
            }
          />
        </div>
      )}

      <Dialog
        header={
          isAddMode
            ? 'Add Performance Standard'
            : `Edit Performance Standard${
                selectedStandard?.measureNumber
                  ? ` - ${selectedStandard.measureNumber}`
                  : ''
              }`
        }
        visible={editDialogVisible}
        style={{
          width: '700px',
          maxWidth: '90vw'
        }}
        onHide={() => {
          setEditDialogVisible(false);
          setIsAddMode(false);
        }}
        footer={
          <div>
            <Button
              label="Cancel"
              onClick={() => {
                setEditDialogVisible(false);
                setIsAddMode(false);
              }}
              className="p-button-text"
            />

            <Button
              label="Save"
              onClick={handleSave}
              loading={loading}
              disabled={loading}
              autoFocus
            />
          </div>
        }
      >
        <SmaEditForm
          editStandard={selectedStandard}
          setEditStandard={setSelectedStandard}
          tiers={tiers}
          measures={measures}
          allStandards={allStandards}
          performanceAreas={performanceAreas}
          measureStatuses={measureStatuses}
          compositeGroups={compositeGroups}
          providerTypesOptions={providerTypeOptions}
        />
      </Dialog>

      <Dialog
        header="Confirm Delete"
        visible={deleteDialogVisible}
        style={{ width: '350px' }}
        onHide={() =>
          setDeleteDialogVisible(false)
        }
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8
            }}
          >
            <Button
              label="Yes"
              autoFocus={false}
              onClick={handleDelete}
              className="p-button-danger"
            />

            <Button
              label="No"
              autoFocus
              onClick={() =>
                setDeleteDialogVisible(false)
              }
              className="p-button-secondary"
            />
          </div>
        }
      >
        <div
          style={{
            fontSize: 16,
            margin: '16px 0'
          }}
        >
          Are you sure you want to delete this record
          {standardToDelete?.measureNumber
            ? `: ${standardToDelete.measureNumber}`
            : ''}
          ?
        </div>
      </Dialog>
    </div>
  );
};

interface SmaEditFormProps {
  editStandard: any;
  setEditStandard: React.Dispatch<
    React.SetStateAction<any>
  >;
  tiers: any[];
  measures: any[];
  allStandards: any[];
  performanceAreas: any[];
  measureStatuses: any[];
  compositeGroups: any[];
  providerTypesOptions: {
    label: string;
    value: string;
  }[];
}

const SmaEditForm: React.FC<SmaEditFormProps> = ({
  editStandard,
  setEditStandard,
  tiers,
  measures,
  allStandards,
  performanceAreas,
  measureStatuses,
  compositeGroups,
  providerTypesOptions
}) => {
  const [reviewYears, setReviewYears] = useState<any[]>([]);

  useEffect(() => {
    const providerType = editStandard?.providerType;

    if (!providerType) {
      setReviewYears([]);
      return;
    }

    let isCurrent = true;

    httpService
      .get(
        `${ApiEndpoints.GetAllActiveReviewYearDetails}/${encodeURIComponent(providerType)}`
      )
      .then((response: any) => {
        if (isCurrent) {
          setReviewYears(
            Array.isArray(response.data)
              ? response.data
              : []
          );
        }
      })
      .catch((error: any) => {
        logger.error(
          'SmaEditForm.GetAllActiveReviewYearDetails failed',
          error
        );

        if (isCurrent) {
          setReviewYears([]);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [editStandard?.providerType]);

  React.useEffect(() => {
    if (!editStandard) {
      return;
    }

    const resolvedNames: Record<string, string> = {};

    const resolveName = (
      idField: string,
      nameField: string,
      list: any[],
      idKey: string,
      nameKey: string
    ) => {
      if (
        editStandard[idField] &&
        !editStandard[nameField]
      ) {
        const item = list.find(
          (option) =>
            String(option[idKey]) ===
            String(editStandard[idField])
        );

        if (item) {
          resolvedNames[nameField] = item[nameKey];
        }
      }
    };

    resolveName(
      'tierId',
      'tierName',
      tiers,
      'tierId',
      'tierName'
    );

    resolveName(
      'standardId',
      'standardTitle',
      allStandards,
      'standardId',
      'title'
    );

    resolveName(
      'measureId',
      'measureNumber',
      measures,
      'measureId',
      'measureNumber'
    );

    resolveName(
      'performanceAreaId',
      'performanceAreaTitle',
      performanceAreas,
      'performanceAreaId',
      'performanceAreaTitle'
    );

    resolveName(
      'measureStatusId',
      'measureStatusName',
      measureStatuses,
      'measureStatusId',
      'measureStatus'
    );

    resolveName(
      'compositeGroupId',
      'compositeGroupName',
      compositeGroups,
      'compositeGroupId',
      'groupName'
    );

    if (Object.keys(resolvedNames).length > 0) {
      setEditStandard({
        ...editStandard,
        ...resolvedNames
      });
    }
  }, [
    editStandard,
    tiers,
    allStandards,
    measures,
    performanceAreas,
    measureStatuses,
    compositeGroups,
    setEditStandard
  ]);

  const filteredPerformanceAreas =
    performanceAreas.filter(
      (pa) =>
        (pa.isActive &&
          (!editStandard?.providerType ||
            pa.providerType ===
              editStandard.providerType)) ||
        String(pa.performanceAreaId) ===
          String(editStandard?.performanceAreaId) ||
        pa.performanceAreaTitle ===
          editStandard?.performanceAreaTitle
    );

  const filteredTiers = tiers.filter(
    (t) =>
      (t.isActive &&
        (!editStandard?.providerType ||
          t.providerType ===
            editStandard.providerType)) ||
      String(t.tierId) ===
        String(editStandard?.tierId) ||
      t.tierName === editStandard?.tierName
  );

  const filteredStandards =
    allStandards.filter(
      (s) =>
        (s.isActive &&
          (!editStandard?.providerType ||
            s.providerType ===
              editStandard.providerType) &&
          (!editStandard?.performanceAreaId ||
            s.performanceAreaId ==
              editStandard.performanceAreaId)) ||
        String(s.standardId) ===
          String(editStandard?.standardId) ||
        s.title === editStandard?.standardTitle
    );

  const filteredMeasures = measures.filter(
    (m) =>
      (m.isActive &&
        (!editStandard?.providerType ||
          m.providerType ===
            editStandard.providerType)) ||
      String(m.measureId) ===
        String(editStandard?.measureId) ||
      m.measureNumber ===
        editStandard?.measureNumber
  );

  return (
    <DataManagementForm
      fields={dataManagementFormConfig.measureMapping}
      values={editStandard}
      onChange={setEditStandard}
      options={{
        providerType:
          providerTypesOptions.map(
            (option) => ({
              ...option,
              id: option.value
            })
          ),

        reviewYear: reviewYears.map(
          (reviewYear) => ({
            label: String(
              reviewYear.reviewYear ??
                reviewYear.ReviewYear ??
                ''
            ),
            value:
              reviewYear.reviewYear ??
              reviewYear.ReviewYear,
            id:
              reviewYear.reviewConfigId ??
              reviewYear.reviewConfigurationId ??
              reviewYear.ReviewConfigurationId
          })
        ),

        performanceAreaTitle:
          filteredPerformanceAreas.map(
            (area) => ({
              label: area.performanceAreaTitle,
              value: area.performanceAreaTitle,
              id: area.performanceAreaId
            })
          ),

        tierName: filteredTiers.map(
          (tier) => ({
            label: tier.tierName,
            value: tier.tierName,
            id: tier.tierId
          })
        ),

        standardTitle:
          filteredStandards.map(
            (standard) => ({
              label: standard.title,
              value: standard.title,
              id: standard.standardId
            })
          ),

        measureNumber:
          filteredMeasures.map(
            (measure) => ({
              label: measure.measureNumber,
              value: measure.measureNumber,
              id: measure.measureId
            })
          ),

        measureStatusName:
          measureStatuses.map(
            (status) => ({
              label: status.measureStatus,
              value: status.measureStatus,
              id: status.measureStatusId
            })
          ),

        compositeGroupName:
          compositeGroups.map(
            (group) => ({
              label: group.groupName,
              value: group.groupName,
              id: group.compositeGroupId
            })
          )
      }}
    />
  );
};

export default SmaComponent;